import React from "react";
import { useParams, Link, useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, ExternalLink, User, Calendar, Activity, Users, CheckCircle, Clock, AlertCircle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";

interface Project {
  id: string;
  name: string;
  description: string;
  client_org: string;
  status: string;
  owner_email: string;
  owner_name: string;
  created_at: string;
  updated_at: string;
  module_completion: {
    maturity: number;
    roi: number;
    value_engineering: number;
  };
  collaborators: string[];
}

export default function AdminProjectDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();

  const { data: project, isLoading, error } = useQuery({
    queryKey: ['/api/projects', id],
    queryFn: async () => {
      const response = await fetch(`/api/projects/${id}`);
      if (!response.ok) throw new Error('Failed to fetch project');
      return response.json();
    },
    enabled: !!id
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      'Active': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
      'In Progress': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
      'Completed': { color: 'bg-gray-100 text-gray-800 border-gray-200', icon: CheckCircle },
      'On Hold': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle },
    };
    return statusConfig[status as keyof typeof statusConfig] || statusConfig['In Progress'];
  };

  const getModuleProgress = (completion: any) => {
    if (!completion) return 0;
    const total = completion.maturity + completion.roi + completion.value_engineering;
    return Math.round(total / 3);
  };

  const getModuleStatus = (progress: number) => {
    if (progress === 0) return { label: 'Not Started', color: 'bg-gray-100 text-gray-800' };
    if (progress < 50) return { label: 'In Progress', color: 'bg-blue-100 text-blue-800' };
    if (progress < 100) return { label: 'Nearly Complete', color: 'bg-yellow-100 text-yellow-800' };
    return { label: 'Complete', color: 'bg-green-100 text-green-800' };
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0D1224] text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Activity className="w-8 h-8 animate-spin mx-auto mb-4 text-[#4AE2C9]" />
              <p>Loading project details...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-[#0D1224] text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-8">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <p className="text-red-400">
              {error ? `Failed to load project: ${error.message}` : 'Project not found'}
            </p>
            <Button asChild className="mt-4 bg-[#4AE2C9] text-[#0D1224] hover:bg-[#4AE2C9]/90">
              <Link to="/admin/projects">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Projects
              </Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const statusConfig = getStatusBadge(project.status);
  const StatusIcon = statusConfig.icon;
  const overallProgress = getModuleProgress(project.module_completion);

  return (
    <div className="min-h-screen bg-[#0D1224] text-white">
      <div className="container mx-auto px-4 py-8">
        
        {/* Header with Navigation */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-4 mb-2">
              <h1 className="text-3xl font-bold text-white">{project.name}</h1>
              <Badge className={statusConfig.color}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {project.status || 'In Progress'}
              </Badge>
            </div>
            <p className="text-gray-400">{project.description}</p>
          </div>
          <div className="flex gap-3">
            <Button asChild variant="outline" className="border-[#4AE2C9] text-[#4AE2C9] hover:bg-[#4AE2C9] hover:text-[#0D1224]">
              <Link to="/admin/projects">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Projects
              </Link>
            </Button>
            <Button 
              onClick={() => setLocation(`/projects/${project.id}`)}
              className="bg-[#4AE2C9] text-[#0D1224] hover:bg-[#4AE2C9]/90"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              View Project
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Project Information */}
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <User className="w-5 h-5 text-[#4AE2C9]" />
                Project Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="text-sm text-gray-400">Client Organization</label>
                  <p className="text-white font-medium">{project.client_org}</p>
                </div>
                
                <div>
                  <label className="text-sm text-gray-400">Project Owner</label>
                  <p className="text-white font-medium">{project.owner_name || 'Unknown'}</p>
                  <p className="text-sm text-gray-400">{project.owner_email}</p>
                </div>

                <div>
                  <label className="text-sm text-gray-400">Project ID</label>
                  <p className="text-xs font-mono text-gray-300 bg-gray-800 px-2 py-1 rounded">
                    {project.id}
                  </p>
                </div>

                <Separator className="bg-gray-700" />

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400">Created</label>
                    <p className="text-white text-sm">
                      {new Date(project.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400">Last Updated</label>
                    <p className="text-white text-sm">
                      {new Date(project.updated_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {project.collaborators && project.collaborators.length > 0 && (
                  <div>
                    <label className="text-sm text-gray-400 flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      Collaborators ({project.collaborators.length})
                    </label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {project.collaborators.map((email, index) => (
                        <Badge key={index} variant="outline" className="text-xs border-gray-600 text-gray-300">
                          {email}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Module Progress */}
          <Card className="bg-gray-900/50 border-gray-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Activity className="w-5 h-5 text-[#4AE2C9]" />
                Module Progress
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Overall Progress */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm text-gray-400">Overall Progress</label>
                  <span className="text-sm text-white font-medium">{overallProgress}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-3">
                  <div 
                    className="bg-[#4AE2C9] h-3 rounded-full transition-all duration-300"
                    style={{ width: `${overallProgress}%` }}
                  />
                </div>
                <Badge className={`mt-2 ${getModuleStatus(overallProgress).color}`}>
                  {getModuleStatus(overallProgress).label}
                </Badge>
              </div>

              <Separator className="bg-gray-700" />

              {/* Individual Modules */}
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-gray-300">Maturity Assessment</label>
                    <span className="text-sm text-white font-medium">
                      {project.module_completion?.maturity || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-blue-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${project.module_completion?.maturity || 0}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-gray-300">ROI Analysis</label>
                    <span className="text-sm text-white font-medium">
                      {project.module_completion?.roi || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-green-400 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${project.module_completion?.roi || 0}%` }}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm text-gray-300">Value Engineering</label>
                    <span className="text-sm text-white font-medium">
                      {project.module_completion?.value_engineering || 0}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-purple h-2 rounded-full transition-all duration-300"
                      style={{ width: `${project.module_completion?.value_engineering || 0}%` }}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Additional Details */}
        <Card className="bg-gray-900/50 border-gray-700 mt-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-[#4AE2C9]" />
              Project Timeline & Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-400">
              <Activity className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Module-level activity details and timeline will be displayed here in future updates.</p>
              <p className="text-sm mt-2">This will include maturity assessment results, value engineering progress, and export history.</p>
            </div>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}